import { useMemo, useState } from 'react'
import { capitalize } from 'lodash'
import { useAuditLogQuery } from '../hooks/useAuditLogQuery'
import { AuditLogFilters } from '../components/AuditLogFilters'
import MobileLogCard from '../components/MobileLogCard'
import { Container } from '@/components/Container'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { formatDate, parseLocalDateString } from '@/utils/timeZoneUtils'
import { useUsersStore } from '@/features/users/store/usersStore'
import { ScrollArea } from '@/components/ui/scroll-area'

export type AuditLogFilters = {
  userId: string
  startDate: string
  endDate: string
  action: string
}

export const initialFilter: AuditLogFilters = {
  userId: 'all',
  startDate: '',
  endDate: '',
  action: 'all',
}

const AuditLogDashboard = () => {
  const users = useUsersStore((s) => s.users)
  const { data: logs, isLoading } = useAuditLogQuery()

  const [filters, setFilters] = useState(initialFilter)

  const logUsers = useMemo(() => {
    if (!logs) return []
    const userIds = Array.from(new Set(logs.map((log) => log.userId)))
    return users.filter((u) => userIds.includes(u._id))
  }, [logs, users])

  const logActions = useMemo(() => {
    if (!logs) return []
    const actions = Array.from(new Set(logs.map((log) => log.action)))
    return actions
  }, [logs])

  const filteredLogs = useMemo(() => {
    if (!logs) return []

    return logs.filter((log) => {
      // User filter (single value)
      if (filters.userId !== 'all' && log.userId !== filters.userId)
        return false

      // Date range filter
      const logDate = new Date(log.timestamp)
      const startDate = filters.startDate
        ? parseLocalDateString(filters.startDate)
        : null
      const endDate = filters.endDate
        ? parseLocalDateString(filters.endDate)
        : null

      if (startDate && logDate < startDate) return false

      if (endDate && logDate > endDate) return false

      // Action filter (single value)
      if (filters.action !== 'all' && log.action !== filters.action) {
        return false
      }

      return true
    })
  }, [logs, filters])

  if (isLoading) {
    return (
      <Container>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto mb-4" />
      </Container>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Audit Logs</h1>
          <p className="text-gray-600 mt-1">
            View and manage the audit logs for your application
          </p>
        </div>
      </div>

      <AuditLogFilters
        onFilter={setFilters}
        goToFirstPage={() => {}}
        logUsers={logUsers}
        logActions={logActions}
      />

      {/* Desktop Table */}
      <ScrollArea className="h-[60vh] hidden lg:flex">
        <div className="hidden lg:block overflow-hidden rounded-md border bg-white">
          <Table>
            <THead>
              <TR>
                <TH>Entity</TH>
                <TH>Action</TH>
                <TH>Date</TH>
                <TH>User</TH>
                <TH>Details</TH>
              </TR>
            </THead>
            <TBody>
              {filteredLogs.map((log) => {
                const logUser = users.find((u) => u._id === log.userId)
                return (
                  <TR key={log._id}>
                    <TD>{log.entity}</TD>
                    <TD>{capitalize(log.action)}</TD>
                    <TD>{formatDate(log.timestamp)}</TD>
                    <TD>{logUser?.name}</TD>
                    <TD>
                      {log.details ? (
                        <pre
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }}
                        >
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      ) : (
                        '-'
                      )}
                    </TD>
                  </TR>
                )
              })}
              {filteredLogs.length === 0 && (
                <TR>
                  <TD className="p-6 text-center text-neutral-500" colSpan={6}>
                    No results
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        </div>
      </ScrollArea>

      {/* Mobile Card List */}
      <div className="block lg:hidden space-y-4 overflow-hidden h-[40vh]">
        {filteredLogs.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 border rounded-md bg-white">
            No results
          </div>
        ) : (
          <ScrollArea className="flex h-full">
            <div className="flex-1 space-y-2 pb-4">
              {filteredLogs.map((log) => {
                const logUser = users.find((u) => u._id === log.userId)
                return (
                  <MobileLogCard key={log._id} log={log} logUser={logUser} />
                )
              })}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  )
}

export default AuditLogDashboard
